import React, { useContext, useState } from 'react';
import { Typography } from 'app/components/ui';
import { TagWithMetadata } from 'app/api/tag-queries';
import { i18n } from 'app/utils/i18n';
import { getTagLabel } from 'app/utils/tag-utils/getTagLabel';
import { TagContext } from 'app/state/contexts/TagContext';
import { TagListItem } from './TagListItem';

interface ChainGroupProps {
  tags: TagWithMetadata[];
  selectedTag: TagWithMetadata | null;
  onSelectTag: (tag: TagWithMetadata | null) => void;
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
  onExtensionClick?: (tagId: string) => void;
  onDeleteClick?: (tag: TagWithMetadata) => void;
}

export const ChainGroup: React.FC<ChainGroupProps> = ({ tags, selectedTag, onSelectTag, onTagDetailsClick, onExtensionClick, onDeleteClick }) => {
  const { tagLanguage } = useContext(TagContext);
  const head = tags[0];
  const containsSelected = tags.some((t) => t.id === selectedTag?.id);
  const [expanded, setExpanded] = useState(false);
  const isExpanded = expanded || containsSelected;
  const label = head?.concept?.labels ? getTagLabel({ languages: head.concept.labels, currentLang: tagLanguage }) : head?.concept?.name || '-';

  const toggle = () => setExpanded((prev) => !prev);

  return (
    <li className="all-tags-item all-tags-chain">
      <div
        className="all-tags-item__header all-tags-chain__header"
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={i18n('tag.continuation.segments')}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <div className="all-tags-item__label all-tags-chain__label">
          <i className={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'} all-tags-chain__chevron`} />
          <Typography variant="body-7">{label || ''}</Typography>
        </div>
        <span className="all-tags-chain__count" aria-label={i18n('tag.continuation.segments')}>
          <Typography variant="body-3" color="inherit">
            {tags.length}
          </Typography>
        </span>
      </div>

      {isExpanded && (
        <ul className="all-tags-chain__segments">
          {tags.map((t) => (
            <TagListItem
              key={t.id}
              tag={t}
              selectedTag={selectedTag}
              onSelectTag={onSelectTag}
              onTagDetailsClick={onTagDetailsClick}
              onExtensionClick={onExtensionClick}
              onDeleteClick={onDeleteClick}
            />
          ))}
        </ul>
      )}
    </li>
  );
};
